import React, { useEffect, useState, useContext } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import Button from "../../../Elements/Button";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import { networkService } from "../../../config/axios";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import UserInfoContext from "../../../../utils/userInfoContext";
import { useLocation } from "react-router-dom";
import DataPerPage from "../../../CommonComponents/Pagination/DataPerPage";
import SearchComponent from "../../../CommonComponents/SearchComponent/SearchComponent";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { AdminFormInput, AdminModalFooter } from "../../../Admin/Shared";
import "../admin.css";
import CustomTooltip from "../../../Elements/Tooltip";

const AdminMasterDomainsPage = () => {
    const { setShowNotifications } = useContext(UserInfoContext);

    const [domains, setDomains] = useState([]);
    const [name, setName] = useState('');
    const [active, setActive] = useState(true);
    const [systemDefault, setSystemDefault] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [dataPerPage, setDataPerPage] = useState(10);
    const totalData = domains?.length;

    const {pathname} = useLocation();
    const {userInfoData} = useContext(UserInfoContext);
    const isAdminRoute = pathname.startsWith('/admin');
    const isWaitingForData =
        !userInfoData || Object.keys(userInfoData).length === 0;
    const userType = userInfoData?.user_type;
    const shouldShowSuperAdminLinks =
        userType === 1 || (isWaitingForData && isAdminRoute);

    const handlePageChange = (pageNumber) => {
        const indexOfLastData = pageNumber * dataPerPage;
        const indexOfFirstData = indexOfLastData - dataPerPage;
        setCurrentPageData(domains?.slice(indexOfFirstData, indexOfLastData));
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    useEffect(() => {
        handlePageChange(1);
    }, [domains, dataPerPage]);

    const fetchDomains = async () => {
        try {
            const res = await networkService('/admin/master_domains.json', 'GET');
            setDomains(res?.data.master_domains || []);
        } catch {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: 'Failed to load domains'
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                master_domain: {
                    name,
                    active,
                    system_default: systemDefault
                }
            };

            if (editingId) {
                await networkService(
                    `/admin/master_domains/${editingId}.json`,
                    'PUT',
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: 'success',
                    toastMessage: 'Domain updated successfully'
                });
            } else {
                await networkService(
                    '/admin/master_domains.json',
                    'POST',
                    payload
                );
                setShowNotifications({
                    showToast: true,
                    toastType: 'success',
                    toastMessage: 'Domain created successfully'
                });
            }

            resetForm();
            fetchDomains();
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage:
                    error?.response?.data?.errors?.join(', ') ||
                    'Something went wrong'
            });
        }
    };

    const handleEdit = (domain) => {
        setEditingId(domain.id);
        setName(domain.name);
        setActive(domain.active);
        setSystemDefault(domain.system_default);
        setShowForm(true);
    };

    const confirmDelete = async () => {
        try {
            await networkService(
                `/admin/master_domains/${deleteId}.json`,
                'DELETE'
            );

            setShowNotifications({
                showToast: true,
                toastType: 'success',
                toastMessage: 'Domain deleted successfully'
            });

            setDeleteId(null);
            fetchDomains();
        } catch {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: 'Failed to delete domain'
            });
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setName('');
        setActive(true);
        setSystemDefault(false);
        setShowForm(false);
    };

    const columns = [
        { Header: 'Domain Name', accessor: 'name' },
        {
            Header: 'Active',
            accessor: 'active',
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
        {
            Header: () => (
                <CustomTooltip title={<p className="p-1 text-light">When enabled, this domain is automatically added to every new user created under this account. Existing users must still be assigned manually.</p>}>
                    <span className="cursor-pointer">Auto-Assign to New Users</span>
                </CustomTooltip>
            ),
            accessor: 'system_default',
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
        {
            Header: 'Owner Type',
            accessor: row => row?.owner?.owner_type
        },
        {
            Header: 'Owner Name',
            accessor: row => row?.owner?.owner_name
        },
        {
            Header: 'Actions',
            Cell: ({ row: { original } }) => (
                <div className="d-flex align-items-center gap-2">
                    <img
                        src="/icons/edit.svg"
                        alt=""
                        className="cursor-pointer admin-domain-action-icon"
                        onClick={() => handleEdit(original)}
                        role="button"
                    />
                    <img
                        src="/icons/delete.svg"
                        alt=""
                        className="cancel cursor-pointer admin-domain-action-icon"
                        onClick={() => setDeleteId(original.id)}
                        role="button"
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <DeleteOrgModal
                title="Are you sure?"
                openModal={deleteId !== null}
                closeModal={() => setDeleteId(null)}
                name="delete this domain"
                handleDelete={confirmDelete}
            />

            <div className="admin-module">
                <div className="admin-domain-page admin-content">
                    <Breadcrumb string="Admin / Domains List" />

                    <div className="container admin-domain-container">
                        <div className="show-select1 d-flex mb-4 admin-domain-header">
                            <div className="admin-domain-controls admin-domain-search-group">
                                {totalData > dataPerPage && <DataPerPage dataPerPage={dataPerPage} setDataPerPage={setDataPerPage} />}
                                <div className="admin-domain-search-input-wrap">
                                    <SearchComponent
                                        data={domains}
                                        setCurrentPageData={setCurrentPageData}
                                        placeholder="Search here"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="admin-domain-search-btn"
                                    aria-label="Search"
                                >
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                </button>
                            </div>

                            {shouldShowSuperAdminLinks && !showForm && (
                                <Button
                                    classList="success"
                                    addonClass="admin-domain-cta"
                                    text="+ Add Domain"
                                    onClick={() => setShowForm(true)}
                                    type="button"
                                    aria-label="Add new domain"
                                />
                            )}
                        </div>

                        {shouldShowSuperAdminLinks && showForm && (
                            <div className="admin-form-page admin-master-domain-form mb-4">
                                <h6 className="admin-form-page-title">
                                    {editingId ? "Edit Domain" : "Add Domain"}
                                </h6>
                                <AdminFormInput
                                    label="Domain name"
                                    name="name"
                                    placeholder="Domain name (example.com)"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                                <label className="admin-form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={active}
                                        onChange={() => setActive(!active)}
                                    />
                                    Active
                                </label>
                                <label className="admin-form-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={systemDefault}
                                        onChange={() => setSystemDefault(!systemDefault)}
                                    />
                                    Auto-Assign to New Users
                                </label>
                                <AdminModalFooter
                                    onCancel={resetForm}
                                    onSubmit={handleSubmit}
                                    cancelText="Cancel"
                                    submitText={editingId ? "Update" : "Create"}
                                    submitType="button"
                                    submitDisabled={!name.trim()}
                                />
                            </div>
                        )}

                        <div className="top-parent admin-domain-table-wrapper">
                            <CustomTable data={currentPageData} columnData={columns}/>
                        </div>

                        {totalData > dataPerPage && (
                            <AdminPagination
                                dataPerPage={dataPerPage}
                                totalData={totalData}
                                onPageChange={handlePageChange}
                                showRange={true}
                                previousLabel="‹ Previous"
                                nextLabel="Next ›"
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminMasterDomainsPage;
